import { FormEvent, useCallback, useEffect, useState } from 'react';
import type { DeviceState } from './data/device-store';
import { useT } from './i18n';
import {
  cancelWalletRecovery,
  completeWalletRecovery,
  loadPendingRecovery,
  loadRecoveryOverview,
  releaseWalletRecovery,
  sendFreshAuthCode,
  startWalletRecovery,
  verifyFreshAuthCode,
  verifyFreshAuthMfa,
  type RecoveryRequestRow,
  type ReleasedRecoveryMaterial,
} from './data/recovery-client';

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function FreshAuthDialog({ email: initialEmail, title, onVerified, onClose }: {
  email?: string;
  title: string;
  onVerified: () => Promise<void> | void;
  onClose: () => void;
}) {
  const { t } = useT();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [code, setCode] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaFactors, setMfaFactors] = useState<Array<{ id: string; name: string }>>([]);
  const [factorId, setFactorId] = useState('');
  const [step, setStep] = useState<'email' | 'code' | 'mfa'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function send() {
    setBusy(true);
    setError('');
    try {
      await sendFreshAuthCode(email);
      setStep('code');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not send the verification email.');
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    setBusy(true);
    setError('');
    try {
      const result = await verifyFreshAuthCode(email, code);
      if (result.needsMfa) {
        if (result.totpFactors.length === 0) {
          throw new Error('Multi-factor verification is required, but no supported authenticator-app factor is available.');
        }
        setMfaFactors(result.totpFactors);
        setFactorId(result.totpFactors[0].id);
        setStep('mfa');
        return;
      }
      await onVerified();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Account verification failed.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyMfa() {
    setBusy(true);
    setError('');
    try {
      await verifyFreshAuthMfa(factorId, mfaCode);
      await onVerified();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Multi-factor verification failed.');
    } finally {
      setBusy(false);
    }
  }

  return <div className="overlay" role="presentation" onMouseDown={() => { if (!busy) onClose(); }}>
    <section className="sheet recovery-dialog" role="dialog" aria-modal="true" aria-labelledby="fresh-auth-title" onMouseDown={(event) => event.stopPropagation()}>
      <div className="sheet-header"><h2 id="fresh-auth-title">{title}</h2><button type="button" onClick={onClose} disabled={busy}>{t('action.close')}</button></div>
      <p className="field-hint">{t('recover.freshVerification')}</p>
      {step === 'email' && <>
        <label>{t('recover.accountEmail')}<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <button className="primary-button" type="button" disabled={busy || !email.trim()} onClick={() => void send()}>{busy ? 'Sending…' : 'Send verification code'}</button>
      </>}
      {step === 'code' && <>
        <label>{t('recover.emailCode')}<input autoFocus inputMode="numeric" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 8))} /></label>
        <button className="primary-button" type="button" disabled={busy || code.length < 6} onClick={() => void verify()}>{busy ? 'Verifying…' : 'Verify account'}</button>
        <button className="quiet-link" type="button" disabled={busy} onClick={() => void send()}>{t('recover.sendAnother')}</button>
      </>}
      {step === 'mfa' && <>
        {mfaFactors.length > 1 && <label>{t('recover.authenticator')}<select value={factorId} onChange={(event) => setFactorId(event.target.value)}>{mfaFactors.map((factor) => <option key={factor.id} value={factor.id}>{factor.name}</option>)}</select></label>}
        <label>{t('recover.authenticatorCode')}<input autoFocus inputMode="numeric" value={mfaCode} onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, '').slice(0, 8))} /></label>
        <button className="primary-button" type="button" disabled={busy || mfaCode.length < 6} onClick={() => void verifyMfa()}>{busy ? 'Verifying…' : 'Verify MFA'}</button>
      </>}
      {error && <p className="form-error" role="alert">{error}</p>}
    </section>
  </div>;
}

export function WalletRecoveryScreen({ onCancel, onRecovered }: {
  onCancel: () => void;
  onRecovered: (device: DeviceState, vaultRootKey: Uint8Array) => Promise<void>;
}) {
  const { t } = useT();
  const [email, setEmail] = useState('');
  const [freshAuthOpen, setFreshAuthOpen] = useState(false);
  const [pending, setPending] = useState<Awaited<ReturnType<typeof loadPendingRecovery>>>(null);
  const [request, setRequest] = useState<RecoveryRequestRow | null>(null);
  const [released, setReleased] = useState<ReleasedRecoveryMaterial | null>(null);
  const [pin, setPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [deviceName, setDeviceName] = useState(`${navigator.platform || 'Browser'} recovery device`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    const stored = await loadPendingRecovery();
    setPending(stored);
    if (stored) {
      setEmail(stored.ownerEmail);
      const overview = await loadRecoveryOverview(stored.vaultId);
      setRequest(overview.requests.find((item) => item.id === stored.requestId) ?? null);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  async function afterFreshAuth() {
    if (!pending) {
      setBusy(true);
      setError('');
      try {
        const started = await startWalletRecovery(email);
        setMessage(`Recovery started. Fingerprint: ${started.fingerprint}`);
        await refresh();
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    setError('');
    try {
      setReleased(await releaseWalletRecovery(pending.requestId));
    } finally {
      setBusy(false);
    }
  }

  async function finish(event: FormEvent) {
    event.preventDefault();
    if (!released) return;
    if (!/^\d{6,12}$/.test(pin)) return setError('Use a 6 to 12 digit app PIN.');
    if (pin !== confirmation) return setError('The two PIN entries do not match.');
    if (!deviceName.trim()) return setError('Enter a device name.');
    setBusy(true);
    setError('');
    try {
      const result = await completeWalletRecovery({ released, pin, deviceName });
      await onRecovered(result.device, result.vaultRootKey);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not complete wallet recovery.');
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!pending) return onCancel();
    setBusy(true);
    try {
      await cancelWalletRecovery(pending.requestId);
      onCancel();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not cancel recovery.');
    } finally {
      setBusy(false);
    }
  }

  if (released) return <main className="gate-shell"><section className="gate-card"><p className="brand">{t('recover.walletTitle')}</p><h1>{t('recover.setNewPin')}</h1><p className="gate-copy">{t('recover.keyRebuilt')}</p><form className="gate-form" onSubmit={finish}><label>{t('recover.deviceName')}<input value={deviceName} onChange={(event) => setDeviceName(event.target.value)} /></label><label>{t('recover.newPin')}<input className="concealed-input" inputMode="numeric" value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 12))} /></label><label>{t('recover.repeatPin')}<input className="concealed-input" inputMode="numeric" value={confirmation} onChange={(event) => setConfirmation(event.target.value.replace(/\D/g, '').slice(0, 12))} /></label>{error && <p className="form-error">{error}</p>}<button className="primary-button" disabled={busy} type="submit">{busy ? 'Finishing…' : 'Recover wallet'}</button></form></section></main>;

  return <main className="gate-shell"><section className="gate-card"><button className="quiet-link" type="button" onClick={() => void cancel()}>Back</button><p className="brand">{t('recover.walletTitle')}</p><h1>{pending ? 'Recovery in progress' : 'Recover wallet access'}</h1>
    {!pending && <><p className="gate-copy">{t('recover.useOnlyWhen')}</p><label>{t('recover.accountEmail')}<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label><button className="primary-button" disabled={!email.trim() || busy} type="button" onClick={() => setFreshAuthOpen(true)}>{t('recover.verifyStart')}</button></>}
    {pending && <><p className="eyebrow">{t('recover.deviceFingerprint')}</p><p className="pairing-fingerprint">{pending.fingerprint}</p><p className="gate-copy">{t('recover.readFingerprint')}</p>{request && <div className="recovery-progress"><p><strong>{t('recover.status')}</strong> {request.state}</p><p><strong>{t('recover.waitingEnds')}</strong> {formatDateTime(request.delay_until)}</p><p><strong>{t('recover.expires')}</strong> {formatDateTime(request.expires_at)}</p></div>}<button className="primary-button" disabled={busy || !request || !['approved', 'released'].includes(request.state) || new Date(request.delay_until).getTime() > Date.now()} type="button" onClick={() => setFreshAuthOpen(true)}>{t('recover.verifyContinue')}</button><button className="danger-link" disabled={busy} type="button" onClick={() => void cancel()}>{t('recover.cancel')}</button></>}
    {message && <p className="status-message">{message}</p>}{error && <p className="form-error">{error}</p>}
    {freshAuthOpen && <FreshAuthDialog email={email} title={pending ? t('recover.continueTitle') : t('recover.startTitle')} onClose={() => setFreshAuthOpen(false)} onVerified={afterFreshAuth} />}
  </section></main>;
}
